// Copyright 2025. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import { connect } from 'react-redux';
import { RootState } from 'admin/core/store';
import {
    bindActionCreators,
    Dispatch,
} from 'redux';
import { LOADING_FLAGS } from 'common/modules/app/loadingFlags/constants';
import {
    requiredRule,
    validate,
} from 'common/validator';
import * as defaultTemplateActions from 'admin/defaultTemplate/actions';
import * as formErrorsActions from 'common/modules/app/formErrors/actions';
import {
    Form,
    FormFieldText,
    Section,
    setIn,
    Translate,
} from '@plesk/ui-library';
import {
    INTENT_TYPE,
    SIZE,
} from 'common/constants';
import { Button } from 'admin/common/components/Button/Button';
import { nestStringProperties } from 'common/modules/app/formErrors/selectors';
import { IDefaultTemplateRequest } from 'common/api/resources/DefaultTemplate/model';

interface IDefaultTemplateFormProps {
    onSubmit: () => void;
}

export type DefaultTemplateFormProps =
    IDefaultTemplateFormProps &
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

export const DefaultTemplateForm: React.FC<DefaultTemplateFormProps> = ({
    item,
    isItemSaving,
    formErrors,
    formErrorsActions: {
        setFormErrors,
        clearFormErrors,
    },
    defaultTemplateActions: {
        unsetDefaultTemplateItem,
        createDefaultTemplate,
        updateDefaultTemplate,
    },
    onSubmit,
}) => {
    const [submitValues, setSubmitValues] = React.useState({ ...item });

    React.useEffect(() => () => {
        clearFormErrors();
        unsetDefaultTemplateItem();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFieldChange = (field: string, value: string) => setSubmitValues(setIn(submitValues, field, value));

    const handleSubmit = async (values: IDefaultTemplateRequest) => {
        const errors = validate<IDefaultTemplateRequest>(values, {
            name: requiredRule(<Translate content="validate.fieldRequired" />),
        });

        if (Object.keys(errors).length) {
            setFormErrors(errors);
            return;
        }

        item.id
            ? await updateDefaultTemplate(item.id, values)
            : await createDefaultTemplate(values);
        onSubmit();
    };

    return (
        <>
            <Form
                id="defaultTemplateForm"
                onSubmit={handleSubmit}
                values={submitValues}
                onFieldChange={handleFieldChange}
                footerClassName="hidden"
                errors={formErrors}
                hideRequiredLegend={true}
                submitButton={false}
                cancelButton={false}
                applyButton={false}
                vertical={true}
            >
                <Section>
                    <FormFieldText
                        name="name"
                        size={SIZE.FILL}
                        label={<Translate content="defaultTemplate.form.name" />}
                        required={true}
                    />
                </Section>
            </Form>
            <Button
                type="submit"
                form="defaultTemplateForm"
                fill={true}
                intent={INTENT_TYPE.PRIMARY}
                size={SIZE.LG}
                isLoading={isItemSaving}
            >
                <Translate content="defaultTemplate.form.saveBtn" />
            </Button>
        </>
    );
};

const mapStateToProps = (state: RootState) => ({
    item: state.defaultTemplate.item,
    isItemSaving: state.app.loadingFlags.has(LOADING_FLAGS.DEFAULT_TEMPLATE_ITEM_SAVE),
    formErrors: nestStringProperties(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    defaultTemplateActions: bindActionCreators(defaultTemplateActions, dispatch),
    formErrorsActions: bindActionCreators(formErrorsActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(DefaultTemplateForm);
